/**
 * Login Page with Cognito Authentication
 * Split-screen aerospace-themed design inspired by Stitch redesign
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth-service';
import { Button, Input, Label } from '@/components/ui';
import { TERMS_CONFIG } from '@aerotage/shared';

const WEBSITE_URL = import.meta.env.VITE_WEBSITE_URL as string;
const TERMS_URL = `${WEBSITE_URL}${TERMS_CONFIG.TERMS_PATH}`;
const PRIVACY_URL = `${WEBSITE_URL}${TERMS_CONFIG.PRIVACY_PATH}`;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordResetSuccess = (location.state as { passwordReset?: boolean } | null)?.passwordReset;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.signIn(email, password);
      window.location.href = '/';
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    authService.initiateGoogleSignIn();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 flex flex-col md:flex-row">
        {/* Left Branding Panel (60%) — hidden on mobile */}
        <section className="hidden md:flex md:w-3/5 relative overflow-hidden items-center justify-center p-16 bg-muted dark:bg-background">
          {/* Atmospheric glow accents */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-info/5 rounded-full blur-[100px]" />

          {/* Branding content */}
          <div className="relative z-10 max-w-2xl">
            {/* System status indicator */}
            <div className="mb-8 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-semibold tracking-[0.2em] text-primary uppercase">
                System Status: Nominal
              </span>
            </div>

            {/* Logo — swap SVG variant per theme to preserve brand green swoosh */}
            <img
              src="/aerotage-logo.svg"
              alt="Aerotage"
              className="h-20 w-auto mb-6 dark:hidden"
            />
            <img
              src="/aerotage-logo-dark.svg"
              alt="Aerotage"
              className="h-20 w-auto mb-6 hidden dark:block"
            />

            {/* Tagline */}
            <p className="text-xl text-muted-foreground font-light leading-relaxed max-w-lg">
              Precision-Engineered Compliance for multi-billion dollar engineering programs.
            </p>

            {/* Feature highlights */}
            <div className="mt-16 grid grid-cols-2 gap-8 border-t border-border/20 pt-8">
              <div>
                <div className="text-[10px] font-semibold text-primary uppercase tracking-widest mb-1">
                  Architecture
                </div>
                <div className="text-sm font-medium text-foreground">
                  Distributed Ledger Compliance
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-primary uppercase tracking-widest mb-1">
                  Validation
                </div>
                <div className="text-sm font-medium text-foreground">
                  Real-time Engineering Specs
                </div>
              </div>
            </div>
          </div>

          {/* Decorative hero image — aerospace schematic, covers full panel */}
          <img
            src="/login-hero.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-15 dark:opacity-10 grayscale dark:brightness-150"
          />
        </section>

        {/* Right Form Panel (40%) */}
        <section className="w-full md:w-2/5 flex flex-col items-center justify-center p-6 md:p-12 lg:p-20 bg-background relative">
          {/* Mobile-only logo — swap SVG variant per theme */}
          <div className="md:hidden absolute top-8 left-8">
            <img
              src="/aerotage-logo.svg"
              alt="Aerotage"
              className="h-10 w-auto dark:hidden"
            />
            <img
              src="/aerotage-logo-dark.svg"
              alt="Aerotage"
              className="h-10 w-auto hidden dark:block"
            />
          </div>

          {/* Login form container */}
          <div className="w-full max-w-md space-y-8">
            {/* Header */}
            <div className="space-y-3 text-center">
              <p className="text-lg text-muted-foreground font-light">
                Welcome to
              </p>
              <img
                src="/aerotage-logo.svg"
                alt="Aerotage"
                className="h-14 w-auto mx-auto dark:hidden"
              />
              <img
                src="/aerotage-logo-dark.svg"
                alt="Aerotage"
                className="h-14 w-auto mx-auto hidden dark:block"
              />
              <p className="text-sm font-medium text-muted-foreground tracking-wide">
                Aerospace Document Management Platform
              </p>
            </div>

            {/* Password Reset Success Message */}
            {passwordResetSuccess && !error && (
              <div className="bg-success/10 border border-success/50 text-success px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Password reset successfully. Please sign in with your new password.</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div role="alert" className="bg-destructive-light border border-destructive text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Sign In Form */}
            <form onSubmit={handleSignIn} className="space-y-6">
              <div className="space-y-1.5">
                <Label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider ml-1">
                  Email Address
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  className="h-12"
                  aria-label="Email address"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <Label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Password
                  </Label>
                  <Button
                    type="button"
                    variant="link"
                    className="text-[11px] h-auto p-0 font-medium tracking-wide text-foreground"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </Button>
                </div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  autoComplete="current-password"
                  className="h-12"
                  aria-label="Password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/30" />
              </div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em]">
                <span className="bg-background px-4 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google Sign In */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              className="w-full h-12 text-base font-medium"
              aria-label="Sign in with Google"
            >
              {/* Google brand colors per https://developers.google.com/identity/branding-guidelines
                  These are INTENTIONALLY HARDCODED - do not change */}
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>

            {/* Legal text */}
            <p className="text-[11px] leading-relaxed text-center text-muted-foreground px-6">
              By signing in, you agree to our{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
                Privacy Policy
              </a>
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative border-t border-border/10 py-6 px-12 bg-card/80 backdrop-blur-sm">
        <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground">
            &copy; {new Date().getFullYear()} Aerotage. Proprietary and Confidential.
          </div>
          <div className="flex gap-8">
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors">
              Legal
            </a>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors">
              Privacy
            </a>
            <a href={`${WEBSITE_URL}/security`} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors">
              Security
            </a>
            <a href={`${WEBSITE_URL}/docs`} target="_blank" rel="noopener noreferrer" className="text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors">
              Documentation
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
